import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {TeamModel} from '../../../../libs/shared/core/src/models/api/teamModel';
import {computeIsGlobalNamespace} from '../../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {TeamAvatar} from '../../../../libs/shared/ui/src/components/avatar/teamAvatar';
import {DropdownBoxCoordinator} from '../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverTransferProps} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {ellipsis} from '../../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {NamespaceDropdown} from '../../../components/common/dropdowns/namespace/namespaceDropdown';

/*
 * Props.
 */

interface AnalyticsNamespaceDropdownBoxProps {
  selectedNamespace?: string;
  teams: ReadonlyArray<TeamModel>;
  userNamespace: string;
  onNamespaceSelect: (arg: string) => void;
  isDisabled?: boolean;
  placeholder?: ReactNode;
  isRequired?: boolean;
  label?: ReactNode;
  backgroundColor?: string;
  userNamespaceLabel?: ReactNode;
  shouldIncludeCompanyDropdownItem: boolean;
}

/*
 * Style.
 */

const StyledDropdownBoxCoordinator = styled(DropdownBoxCoordinator)`
  padding-top: 0;
`;

const StyledValueDiv = styled.div`
  display: flex;
  align-items: center;
`;

const StyledIconDiv = styled.div`
  flex-shrink: 0;
  margin-right: 8px;
`;

const StyledNameDiv = styled.div`
  ${ellipsis()};
`;

const StyledPlaceholderDiv = styled.div`
  color: ${(p) => p.theme.greys.shade60};
  ${ellipsis()};
`;

/*
 * Component.
 */

export const AnalyticsNamespaceDropdownBox: FC<AnalyticsNamespaceDropdownBoxProps> = (props) => {
  const {isRequired, backgroundColor, teams, shouldIncludeCompanyDropdownItem} = props;

  const isDisabled = props.isDisabled || (teams.length === 0 && !shouldIncludeCompanyDropdownItem);

  // Avoid background-color flickering when clicking on the value when dropdown is open.
  const shouldPreventMouseDown = Boolean(backgroundColor);

  return (
    <StyledDropdownBoxCoordinator
      isDisabled={isDisabled}
      value={renderValue(props)}
      renderDropdown={(popoverProps) => renderDropdown(popoverProps, props)}
      isRequired={isRequired}
      backgroundColor={backgroundColor}
      shouldPreventMouseDown={shouldPreventMouseDown}
    />
  );
};

function renderValue(props: AnalyticsNamespaceDropdownBoxProps) {
  const {selectedNamespace, placeholder, userNamespaceLabel, teams} = props;
  const team = teams.find((t) => t.namespace === selectedNamespace);

  // If no selectedNamespace, render the placeholder, if one exists.
  if (!selectedNamespace) {
    return (
      <StyledValueDiv>
        <StyledPlaceholderDiv>
          {placeholder || <Trans id="team-namespace-dropdown-placeholder">Select workspace</Trans>}
        </StyledPlaceholderDiv>
      </StyledValueDiv>
    );
  }

  // Render the company avatar if this is the global namespace.
  if (computeIsGlobalNamespace(selectedNamespace)) {
    return (
      <UserThemeConsumer>
        {(theme) => (
          <StyledValueDiv>
            <StyledIconDiv>
              <Icon name="buildingFilledSmall" color={theme.greys.shade70} />
            </StyledIconDiv>
            <StyledNameDiv>
              <Trans id="analytics-namespace-global-selection">Company</Trans>
            </StyledNameDiv>
          </StyledValueDiv>
        )}
      </UserThemeConsumer>
    );
  }

  // If no team was found, this is the private namespace.
  if (!team) {
    return (
      <UserThemeConsumer>
        {(theme) => (
          <StyledValueDiv>
            <StyledIconDiv>
              <Icon name="lockSmall" color={theme.greys.shade70} />
            </StyledIconDiv>
            <StyledNameDiv>{renderUserItemName(userNamespaceLabel)}</StyledNameDiv>
          </StyledValueDiv>
        )}
      </UserThemeConsumer>
    );
  }

  // Otherwise, render the selected team.
  return (
    <StyledValueDiv>
      <StyledIconDiv>
        <TeamAvatar team={team} size={VisualSizesEnum.SMALL} />
      </StyledIconDiv>
      <StyledNameDiv>{team.name}</StyledNameDiv>
    </StyledValueDiv>
  );
}

const renderUserItemName = (userNamespaceLabel?: ReactNode) =>
  userNamespaceLabel || <Trans id="dropdown-analytics-namespace-me">Me</Trans>;

function renderDropdown(popoverProps: PopoverTransferProps, props: AnalyticsNamespaceDropdownBoxProps) {
  const {
    selectedNamespace,
    userNamespace,
    userNamespaceLabel,
    teams,
    onNamespaceSelect,
    shouldIncludeCompanyDropdownItem,
  } = props;
  const {anchor, anchorWidth, onRequestClose} = popoverProps;

  return (
    <NamespaceDropdown
      selectedNamespace={selectedNamespace}
      userNamespace={userNamespace}
      teams={teams}
      renderUserItemName={() => renderUserItemName(userNamespaceLabel)}
      shouldRenderCompanyDropdownItem={shouldIncludeCompanyDropdownItem}
      onNamespaceSelect={onNamespaceSelect}
      anchor={anchor}
      width={anchorWidth}
      onRequestClose={onRequestClose}
    />
  );
}
